import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { User, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const LOGO_HORIZONTAL = null; // Using text logo instead

const navLinks = [
  { name: 'Accueil', path: '/', num: '01' },
  { name: 'Services', path: '/services', num: '02' },
  { name: 'Réalisations', path: '/portfolio', num: '03' },
  { name: 'Tarifs', path: '/tarifs', num: '04' },
  { name: 'Contact', path: '/contact', num: '05' },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 w-full z-50 nav-blur" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-24 md:h-28">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 relative z-[60]" data-testid="nav-logo">
              <img 
                src="https://customer-assets-jt897jd0.emergentagent.net/job_webisula-digital/artifacts/fabbc332efdcbb6a_sphere.png" 
                alt="" 
                className="h-11 md:h-14 w-auto sphere-spin"
              />
              <span className="text-2xl md:text-3xl font-black tracking-tight">
                <span className="text-white">WEB</span><span className="text-[#8B5A8B]">ISULA</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                  className={`text-sm font-medium transition-colors duration-200 relative group ${
                    location.pathname === link.path
                      ? 'text-white'
                      : 'text-white/70 hover:text-white'
                  }`}
                >
                  <span className="text-[#8B5A8B]/50 text-[10px] font-mono absolute -top-3 left-0">{link.num}</span>
                  {link.name}
                </Link>
              ))}
            </div>

            {/* CTA & Auth (Desktop) */}
            <div className="hidden lg:flex items-center space-x-4">
              {isAuthenticated ? (
                <div className="flex items-center space-x-4">
                  <Link
                    to="/dashboard"
                    data-testid="nav-dashboard-btn"
                    className="flex items-center space-x-2 text-white/70 hover:text-white transition-colors"
                  >
                    <User size={18} />
                    <span className="text-sm">{user?.name}</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    data-testid="nav-logout-btn"
                    className="flex items-center space-x-2 text-white/70 hover:text-white transition-colors"
                  >
                    <LogOut size={18} />
                  </button>
                </div>
              ) : (
                <>
                  <Link
                    to="/login"
                    data-testid="nav-login-btn"
                    className="text-sm text-white/70 hover:text-white transition-colors"
                  >
                    Connexion
                  </Link>
                  <Link
                    to="/register"
                    data-testid="nav-register-btn"
                    className="glow-btn text-sm px-6 py-3"
                  >
                    Créer un compte
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu button - Animated hamburger */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden relative z-[60] w-10 h-10 flex items-center justify-center"
              data-testid="nav-mobile-toggle"
              aria-label="Menu"
            >
              <div className="w-7 flex flex-col items-end gap-[6px]">
                <motion.span
                  animate={isOpen ? { rotate: 45, y: 8, width: 28 } : { rotate: 0, y: 0, width: 28 }}
                  transition={{ duration: 0.3 }}
                  className="block h-[2px] bg-white rounded-full origin-center"
                  style={{ width: 28 }}
                />
                <motion.span
                  animate={isOpen ? { opacity: 0, x: 20 } : { opacity: 1, x: 0 }}
                  transition={{ duration: 0.2 }}
                  className="block h-[2px] bg-white rounded-full"
                  style={{ width: 20 }}
                />
                <motion.span
                  animate={isOpen ? { rotate: -45, y: -8, width: 28 } : { rotate: 0, y: 0, width: 28 }}
                  transition={{ duration: 0.3 }}
                  className="block h-[2px] bg-white rounded-full origin-center"
                  style={{ width: 14 }}
                />
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* ═══════════════ FULL-SCREEN MOBILE MENU ═══════════════ */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 lg:hidden"
            data-testid="nav-mobile-menu"
          >
            {/* Background */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-[#0C0514]"
            >
              {/* Decorative gradient orb */}
              <div className="absolute top-1/3 right-0 w-[300px] h-[300px] bg-[#8B5A8B]/10 rounded-full blur-[120px]" />
              <div className="absolute bottom-1/4 left-0 w-[200px] h-[200px] bg-[#8B5A8B]/5 rounded-full blur-[80px]" />
            </motion.div>

            {/* Menu content */}
            <div className="relative z-10 h-full flex flex-col justify-between px-8 pt-32 pb-10 overflow-y-auto">
              
              {/* Navigation Links */}
              <div className="space-y-2">
                {navLinks.map((link, i) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: -40 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -40 }}
                    transition={{ delay: i * 0.08, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <Link
                      to={link.path}
                      data-testid={`nav-mobile-link-${link.name.toLowerCase()}`}
                      className={`flex items-baseline gap-4 py-3 group transition-colors duration-200 ${
                        location.pathname === link.path
                          ? 'text-white'
                          : 'text-white/40 hover:text-white'
                      }`}
                    >
                      <span className="text-[#8B5A8B]/60 text-sm font-mono min-w-[28px]">{link.num}</span>
                      <span className="text-[2rem] sm:text-[2.5rem] font-black tracking-tight leading-tight">
                        {link.name}
                      </span>
                      {location.pathname === link.path && (
                        <motion.span
                          layoutId="mobile-active"
                          className="w-2 h-2 bg-[#8B5A8B] rounded-full ml-2 flex-shrink-0"
                        />
                      )}
                    </Link>
                  </motion.div>
                ))}
              </div>

              {/* Bottom section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ delay: 0.4, duration: 0.4 }}
                className="space-y-6"
              >
                {/* Auth links */}
                <div className="flex items-center gap-4 border-t border-white/5 pt-6">
                  {isAuthenticated ? (
                    <>
                      <Link
                        to="/dashboard"
                        data-testid="nav-mobile-dashboard"
                        className="flex items-center gap-2 text-white/50 hover:text-white text-sm transition-colors"
                      >
                        <User size={16} />
                        Mon espace
                      </Link>
                      <button
                        onClick={() => { handleLogout(); setIsOpen(false); }}
                        data-testid="nav-mobile-logout"
                        className="flex items-center gap-2 text-white/50 hover:text-white text-sm transition-colors"
                      >
                        <LogOut size={16} />
                        Déconnexion
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/login"
                        data-testid="nav-mobile-login"
                        className="text-white/50 hover:text-white text-sm transition-colors"
                      >
                        Connexion
                      </Link>
                      <Link
                        to="/register"
                        data-testid="nav-mobile-register"
                        className="glow-btn text-sm px-5 py-2.5"
                      >
                        Créer un compte
                      </Link>
                    </>
                  )}
                </div>

                {/* Contact info */}
                <div className="flex items-center justify-between text-white/30 text-xs">
                  <span>Ajaccio, Corse</span>
                  <a href="tel:0759948289" className="hover:text-white/60 transition-colors">
                    07 59 94 82 89
                  </a>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
